import React, { useId, useState } from 'react';
import { useLionAutoSaleRefill, useSaveLionAutoSaleRefill } from '../../hooks/useLionAuto';
import {
  choiceOf, valueOfChoice, defaultChoiceText, showDefaultText, effectiveText, saleRefillErrorText,
} from '../../utils/lionAutoSaleRefill';

// The event's "Reprice after a sale" box, under Floors: whether a group on
// Auto here re-ladders within a few minutes after one of its listings sells
// (on), or waits for its next scheduled run (off). This show's setting wins,
// else the artist's (the Skybox performer: every show of it), else the
// runner's default (the Auto runner panel's checkbox). Two rows, each
// Default / On / Off with its own Save: the artist (locked while the show's
// performer is not known) and this show. A pick is only a draft until Save
// (a closed select fires a change on each arrow key), with no confirm (it
// changes when a group runs, not whether it writes); a failed save keeps
// the draft for a retry. Every staff member sees what applies; only an
// admin changes it (canEdit, which the server enforces). Our DB only:
// nothing is sent to Skybox.

const ADMIN_ONLY_TITLE = 'Only an admin can change Reprice after a sale';
const UNKNOWN_PERFORMER_TITLE = "This show's performer is not known, so it has no artist setting";
const HEAD_TITLE = 'On: a group on Auto here re-ladders within a few minutes after one of its listings sells. '
  + "Off: it waits for its next scheduled run. This show's setting wins, else the artist's, else the default.";

// One setting: its label, the Default / On / Off select, Save and a hint.
// saved: true | false | null (Default), as the server has it; draft: the
// option picked and not saved yet, null = the saved setting shows. Save is
// on only while the draft differs and no save runs; onSave gets the value
// (true | false | null).
function RefillRow({
  label, labelTitle, saved, draft, onDraft, onSave, defaultText, locked, lockedTitle, busy, hint,
}) {
  const id = useId();
  const savedChoice = choiceOf(saved);
  const shown = draft ?? savedChoice;
  const changed = shown !== savedChoice;
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!locked && !busy && changed) onSave(valueOfChoice(shown));
  };
  let saveTitle = changed ? 'Save this setting (nothing is sent to Skybox)' : 'Pick a new setting first';
  if (locked) saveTitle = lockedTitle;
  return (
    <form className="la-floor-row" onSubmit={handleSubmit}>
      <label className="la-floor-label" htmlFor={id} title={labelTitle}>{label}</label>
      <select
        id={id}
        className="form-input form-input-sm"
        value={shown}
        disabled={locked}
        title={locked ? lockedTitle : undefined}
        onChange={(e) => onDraft(e.target.value)}
      >
        <option value="default">{defaultText}</option>
        <option value="on">On</option>
        <option value="off">Off</option>
      </select>
      <button type="submit" className="btn btn-primary btn-sm la-floor-save" disabled={locked || busy || !changed} title={saveTitle}>
        Save
      </button>
      {hint && <span className="la-hint">{hint}</span>}
    </form>
  );
}

function LaSaleRefillPanel({ eventId }) {
  const refillQ = useLionAutoSaleRefill(eventId);
  const saveM = useSaveLionAutoSaleRefill(eventId);
  // Each select's pick while the admin edits it ('default' | 'on' | 'off');
  // null = the saved setting shows
  const [artistDraft, setArtistDraft] = useState(null);
  const [showDraft, setShowDraft] = useState(null);
  const [error, setError] = useState(null); // the last save's failure, or null

  const v = refillQ.data || null;
  const httpStatus = refillQ.error?.response?.status;

  if (!v) {
    return (
      <div className="la-panel la-floors">
        <div className="la-panel-head">
          <h3 title={HEAD_TITLE}>Reprice after a sale</h3>
          {refillQ.isLoading && <span className="la-muted">reading…</span>}
        </div>
        {refillQ.error && (
          <div className={`la-status${httpStatus === 404 ? '' : ' error'}`}>
            {saleRefillErrorText(refillQ.error, { read: true })}
            {httpStatus !== 404 && (
              <button type="button" className="btn btn-secondary btn-sm la-status-action" onClick={() => refillQ.refetch()}>Retry</button>
            )}
          </div>
        )}
      </div>
    );
  }

  const name = typeof v.artist?.name === 'string' && v.artist.name.trim() ? v.artist.name.trim() : null;
  const canEdit = v.canEdit === true;
  const busy = saveM.isPending;
  const savingScope = busy && saveM.variables ? ('artist' in saveM.variables ? 'artist' : 'show') : null;
  let artistHint = null;
  if (!name && canEdit) artistHint = "This show's performer is not known: set this show only.";
  else if (savingScope === 'artist') artistHint = 'Saving…';

  const handleSave = (scope, value) => {
    if (busy || !canEdit) return;
    const picked = choiceOf(value);
    const resetDraft = scope === 'artist' ? setArtistDraft : setShowDraft;
    setError(null);
    saveM.mutate(scope === 'artist' ? { artist: value } : { show: value }, {
      // A pick changed while this save ran stays on screen
      onSuccess: () => resetDraft((d) => (d === picked ? null : d)),
      onError: (err) => setError(saleRefillErrorText(err)),
    });
  };

  return (
    <div className="la-panel la-floors">
      <div className="la-panel-head">
        <h3 title={HEAD_TITLE}>Reprice after a sale</h3>
        <span className="la-muted">{effectiveText(v.effective, name)}</span>
      </div>
      {refillQ.error && (
        <div className="la-hint warn">
          Could not re-read the setting: this is the last copy.{' '}
          <button type="button" className="la-link" onClick={() => refillQ.refetch()}>Retry</button>
        </div>
      )}

      <div className="la-floors-rows">
        <RefillRow
          label={name ? `${name} (all shows)` : 'Artist (all shows)'}
          labelTitle={name ? `Every ${name} show without a setting of its own` : UNKNOWN_PERFORMER_TITLE}
          saved={name ? v.artist.value : null}
          draft={name ? artistDraft : null}
          onDraft={setArtistDraft}
          onSave={(value) => handleSave('artist', value)}
          defaultText={defaultChoiceText(v.default)}
          locked={!name || !canEdit}
          lockedTitle={name ? ADMIN_ONLY_TITLE : UNKNOWN_PERFORMER_TITLE}
          busy={busy}
          hint={artistHint}
        />
        <RefillRow
          label="This show only"
          labelTitle="This show's groups; it wins over the artist's setting and the default"
          saved={v.show}
          draft={showDraft}
          onDraft={setShowDraft}
          onSave={(value) => handleSave('show', value)}
          defaultText={showDefaultText(v)}
          locked={!canEdit}
          lockedTitle={ADMIN_ONLY_TITLE}
          busy={busy}
          hint={savingScope === 'show' ? 'Saving…' : null}
        />
        {!canEdit && <div className="la-hint">Admins change this</div>}
      </div>
      {error && <div className="la-status error">{error}</div>}
    </div>
  );
}

export default LaSaleRefillPanel;
